<template>
	<PageLoading
		:loading="cases.loading || battles.loading"
		class="relative mt-4"
	>
		<PagePadding class="flex justify-between gap-4">
			<div class="min-w-0">
				<div class="text-label-secondary text-xs font-medium uppercase">
					{{ t('welcome') }}
				</div>
				<div
					class="font-squad mt-2 truncate text-[28px] leading-9 font-black uppercase"
				>
					{{ user.name }}
				</div>
			</div>
			<RouterLink custom to="/rewards" v-slot="{ navigate }">
				<BaseButton
					size="md"
					color="secondary"
					class="relative uppercase"
					@click="navigate"
				>
					<PentagonIcon
						class="absolute! -top-0.5 -left-4.25"
						:active="!!freeCaseCount"
					>
						<div v-if="freeCaseCount">{{ freeCaseCount }}</div>
						<RewardsIcon class="h-5 w-5" v-else />
					</PentagonIcon>
					<div class="pl-6">{{ t('rewards') }}</div>
				</BaseButton>
			</RouterLink>
		</PagePadding>
		<PagePadding class="mt-6">
			<div class="flex gap-2">
				<RouterLink custom to="/cases" v-slot="{ navigate }">
					<BaseCard
						class="flex-1 border-2 border-[#E6992A] p-0.5"
						v-hover="{ active: 'scale-95' }"
						@click="navigate"
					>
						<div
							class="relative flex h-full flex-col items-center overflow-hidden rounded-lg bg-radial-(--cases-gradient) px-5 pt-34 pb-4"
						>
							<img
								:src="CasesIllustration"
								class="absolute top-0 left-1/2 h-50 w-100 max-w-none -translate-x-1/2"
							/>
							<div
								class="font-squad text-label-primary text-center text-[20px] leading-6.5 font-black uppercase"
							>
								{{ t('cases') }}
							</div>
							<div class="mt-1 text-center text-xs font-medium uppercase">
								{{ t('unbox') }}
							</div>
						</div>
					</BaseCard>
				</RouterLink>
				<RouterLink custom to="/battles" v-slot="{ navigate }">
					<BaseCard
						class="flex-1 border-2 border-[#3AA2FA] p-0.5"
						v-hover="{ active: 'scale-95' }"
						@click="navigate"
					>
						<div
							class="relative flex h-full flex-col items-center overflow-hidden rounded-lg bg-radial-(--battles-gradient) px-5 pt-34 pb-4"
						>
							<img
								:src="BattlesIllustration"
								class="absolute top-0 left-1/2 h-50 w-100 max-w-none -translate-x-1/2"
							/>
							<div
								class="font-squad text-label-primary text-center text-[20px] leading-6.5 font-black uppercase"
							>
								{{ t('battles') }}
							</div>
							<div class="mt-1 text-center text-xs font-medium uppercase">
								{{ t('vs') }}
							</div>
						</div>
					</BaseCard>
				</RouterLink>
			</div>
			<SubtitleText class="mt-6">{{ t('featuredCases') }}</SubtitleText>
		</PagePadding>
		<PagePillRow class="mt-2 *:gap-2!">
			<RouterLink
				:to="`/case/${item.uuid}`"
				custom
				v-slot="{ navigate }"
				v-for="(item, i) in casesSliced"
			>
				<CaseCard
					class="w-[calc(50vw-20px)] max-w-56 snap-start snap-always"
					:risk="item.risk"
					:price="item.price"
					:uuid="item.uuid"
					:name="item.name"
					@click="navigate"
				/>
			</RouterLink>
		</PagePillRow>
		<PagePadding class="mt-6">
			<SubtitleText>{{ t('featuredBattles') }}</SubtitleText>
			<div class="mt-2 space-y-1.5">
				<BattleCard
					v-for="item in battlesSorted"
					notOpaque
					:key="item.uuid"
					:uuid="item.uuid"
					:status="item.status"
					:participants="item.participants"
					:cases="item.cases"
					:entryFee="item.entryFee"
					:gameMode="item.gameMode"
					:modifiers="item.modifiers"
					:startedAt="item.ts"
					:tieBreakWinner="item.tieBreakWinner"
					:funding="item.funding"
					@setStatus="setStatus(item.uuid, $event)"
				/>
			</div>
		</PagePadding>
		<LiveDrop :liveDrop="liveDrop" class="mt-6">{{ t('live') }}</LiveDrop>
	</PageLoading>
</template>
<script setup lang="ts">
import CaseCard from '@/components/CaseCard.vue'
import PagePadding from '@/components/PagePadding.vue'
import PageTitle from '@/components/PageTitle.vue'
import { useLayout } from '@/stores/layout'
import PagePillRow from '@/components/PagePillRow.vue'
import { useCases } from '@/stores/cases'
import PageLoading from '@/components/PageLoading.vue'
import { useI18n } from 'vue-i18n'
import { computed, ref, TransitionGroup, watch } from 'vue'
import { useBattles } from '@/stores/battles'
import { useUser } from '@/stores/user'
import SubtitleText from '@/components/SubtitleText.vue'
import BattleCard from '@/components/BattleCard.vue'
import BaseCard from '@/components/BaseCard.vue'
import CasesIllustration from '@/assets/illustrations/cases.png'
import BattlesIllustration from '@/assets/illustrations/battles.png'
import { useLive } from '@/stores/live'
import ItemIcon from '@/components/ItemIcon.vue'
import { animation } from '@/utils/urls'
import CoinPrice from '@/components/CoinPrice.vue'
import BaseButton from '@/components/BaseButton.vue'
import PentagonIcon from '@/components/PentagonIcon.vue'
import RewardsIcon from '@/assets/icons/rewards.svg'
import LiveDrop from '@/components/LiveDrop.vue'

const cases = useCases()
const battles = useBattles()
const user = useUser()
const live = useLive()

const layout = useLayout()

layout.showBottomNavbar()

const { t } = useI18n({
	messages: {
		en: {
			welcome: 'welcome back',
			games: 'our games',
			featuredCases: 'featured cases',
			featuredBattles: 'featured battles',
			cases: 'cases',
			unbox: 'unbox gifts',
			battles: 'battles',
			vs: 'vs players',
			live: 'live wins',
			rewards: 'rewards'
		},
		ru: {
			welcome: 'с возвращением',
			games: 'наши игры',
			featuredCases: 'рекомендуемые кейсы',
			featuredBattles: 'рекомендуемые батлы',
			cases: 'кейсы',
			unbox: 'выбей гифты',
			battles: 'батлы',
			vs: 'против игроков',
			live: 'Последние выигрыши',
			rewards: 'награды'
		}
	}
})

const battlesSorted = computed(() => {
	return battles.list
		.slice(0)
		.sort((a, b) => {
			return b.entryFee - a.entryFee
		})
		.slice(0, 3)
})

const casesSliced = computed(() => {
	return cases.sorted.slice(0, 5)
})

const setStatus = (
	uuid: string,
	status: 'running' | 'finished' | 'created' | 'waiting_for_block'
) => battles.setStatus(uuid, status)

const liveDrop = computed(() => {
	return live.last.map((item) => ({
		modelName: item.modelName,
		src: animation(item.modelUuid),
		price: item.price,
		time: item.time,
		preloader: item.preloader || undefined
	}))
})

const freeCaseCount = computed(
	() =>
		cases.rewardCasesCount +
		(cases.welcomeCase?.count || 0) +
		(cases.referralCase?.canOpen ? 1 : 0)
)
</script>
